import React, { useState } from "react";
import { Suggest } from "react-geosuggest";
import GeoSuggest from "components/GeoSuggest";
import { useQuery, useMutation } from "@apollo/client";
import { Formik } from "formik";
import { inputProps } from "utilities/formik";
import { useTranslation } from "react-i18next";

import Switch from "components/Switch";
import Form from "components/Form";
import Text from "components/Text";
import FormHorizontalGroup from "components/Form/HorizontalGroup";
import BaseFormElement from "components/Form/BaseFormElement";
import ActionFooter from "components/ActionFooter";
import LoadingContainer from "components/LoadingContainer";
import { omitTypeName } from "utilities/connections";

import styles from "./styles.scss";
import talentLocationQuery from "./talentLocationQuery.gql";
import talentLocationMutation from "./talentLocationMutation.gql";
import { isPinata } from "utilities/flavor";
import useRouter from "use-react-router";
import FooterDistance from "components/FooterDistance";
import { getUrlQueries } from "pinata-common/routes";

const i18nBase = "gogetter.settings.locationTravel.";

export default function LocationTravel() {
  const { t } = useTranslation();
  const { data, loading } = useQuery(talentLocationQuery);
  const [updateLocation] = useMutation(talentLocationMutation);
  const { history, location } = useRouter();

  const [error, setError] = useState(false);

  if (loading) return <LoadingContainer />;

  const {
    me: { profile },
  } = data;

  return (
    <div className={styles.container}>
      <Formik
        initialValues={omitTypeName({ ...profile, flexibleTravel: profile?.flexibleTravel ? "flexible" : "limited" })}
        onSubmit={async (values, actions) => {
          try {
            setError(false);

            await updateLocation({
              variables: { input: { ...values, flexibleTravel: values.flexibleTravel === "flexible" } },
              refetchQueries: [{ query: talentLocationQuery }],
            });

            const { next } = getUrlQueries(location.search);

            if (next) {
              setTimeout(() => {
                history.push(next);
              }, 500);
            }

            actions.resetForm(values);
            actions.resetForm({ values });
          } catch (err) {
            setError(true);
            throw err;
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {props => {
          const input = inputProps(props);
          const { values, setFieldValue } = props;

          return (
            <>
              <div className={styles.locAndTravel}>
                <FormHorizontalGroup className={styles.horizontalGroup}>
                  <BaseFormElement
                    testId="gogetter.locationTravel.homeAddress"
                    label={t(`${i18nBase}homeAddressLabel`)}
                    inputClassNameProp="inputClassName"
                    element={
                      <GeoSuggest
                        className={styles.geosuggest}
                        placeholder={t(`${i18nBase}homeAddressPlaceholder`)}
                        country={["us", "ca"]}
                        initialValue={profile?.address || ""}
                        onSuggestSelect={(suggestion: Suggest) => {
                          let shippingAddress: any = {
                            address: null,
                            latitude: null,
                            longitude: null,
                            addressJson: null,
                          };

                          if (suggestion) {
                            const {
                              location: { lat: latitude, lng: longitude },
                              gmaps,
                            } = suggestion;

                            if (!gmaps) return;

                            const { formatted_address: address, address_components } = gmaps;

                            shippingAddress = {
                              address,
                              latitude,
                              longitude,
                              addressJson: address_components,
                            };
                          }
                          Object.entries(shippingAddress).map(([key, value]) => {
                            setFieldValue(key, value);
                          });
                        }}
                      />
                    }
                  />
                  <Form.TextBox
                    {...input("apartment")}
                    label={t(`${i18nBase}apartmentLabel`)}
                    placeholder={t(`${i18nBase}apartmentPlaceholder`)}
                    className={styles.apartmentInput}
                    testId={"gogetter.locationTravel.apartment"}
                  />
                </FormHorizontalGroup>
                <div className={styles.switchInput}>
                  <Switch
                    {...input("shipHome")}
                    id="shipHome"
                    checked={input("shipHome").value}
                    onChange={() => props.setFieldValue("shipHome", !input("shipHome").value)}
                    testId={"gogetter.locationTravel.sameAddressForShipping"}
                  />
                  <Text size={14}>{t(`${i18nBase}sameShippingAddress`)}</Text>
                </div>
                {!values.shipHome && (
                  <BaseFormElement
                    testId={"gogetter.locationTravel.shippingAddress"}
                    label={t(`${i18nBase}shippingAddressLabel`)}
                    inputClassNameProp="inputClassName"
                    className={styles.baseInput}
                    element={
                      <GeoSuggest
                        className={styles.geosuggest}
                        placeholder={t(`${i18nBase}homeAddressPlaceholder`)}
                        country={["us", "ca"]}
                        initialValue={profile?.shippingAddress || ""}
                        onSuggestSelect={(suggestion: Suggest) => {
                          let addressInput: any = { shippingAddress: null, shippingAddressComponents: null };

                          if (suggestion) {
                            const { gmaps } = suggestion;

                            if (!gmaps) return;

                            const { formatted_address: shippingAddress, address_components } = gmaps;

                            addressInput = {
                              shippingAddress,
                              shippingAddressComponents: address_components,
                            };
                          }
                          // @ts-ignore
                          Object.entries(addressInput).map(([key, value]) => {
                            setFieldValue(key, value);
                          });
                        }}
                      />
                    }
                  />
                )}
                <hr className={styles.hr} />
                {isPinata && (
                  <>
                    <div className={styles.switchInput}>
                      <Switch
                        {...input("canDrive")}
                        id="canDrive"
                        checked={input("canDrive").value}
                        onChange={() => props.setFieldValue("canDrive", !input("canDrive").value)}
                        testId={"gogetter.locationTravel.canDrive"}
                      />
                      <Text size={14}>{t(`${i18nBase}canDrive`)}</Text>
                    </div>
                    <div className={styles.switchInput}>
                      <Switch
                        {...input("hasCar")}
                        id="iHaveACar"
                        checked={input("hasCar").value}
                        onChange={() => props.setFieldValue("hasCar", !input("hasCar").value)}
                        testId={"gogetter.locationTravel.haveACar"}
                      />
                      <Text size={14}>{t(`${i18nBase}hasCar`)}</Text>
                    </div>
                    <hr className={styles.hr} />
                    <Form.Dropdown
                      testId={"gogetter.locationTravel.abilityToTravel"}
                      label={t(`${i18nBase}abilityToTravelLabel`)}
                      className={styles.baseInput}
                      {...input("flexibleTravel")}
                    >
                      <option key="flexible" value="flexible">
                        {t(`${i18nBase}flexible`)}
                      </option>
                      <option key="limited" value="limited">
                        {t(`${i18nBase}limited`)}
                      </option>
                    </Form.Dropdown>
                    {props.values.flexibleTravel === "flexible" ? (
                      <Text color={"gray3"} size={14} lineHeight={20} top="m">
                        {t(`${i18nBase}flexibleCopy`)}
                      </Text>
                    ) : (
                      <Form.TextBox
                        {...input("areas")}
                        multiline
                        label={t(`${i18nBase}areasLabel`)}
                        placeholder={t(`${i18nBase}areasPlaceholder`)}
                        inputClassName={styles.areas}
                        testId={"gogetter.locationTravel.areas"}
                      />
                    )}{" "}
                  </>
                )}
                {error && (
                  <Text color="pink1" top="m" bottom="m">
                    {t(`${i18nBase}somethingWentWrong`)}
                  </Text>
                )}
              </div>
              <FooterDistance distance={0} className={styles.footer}>
                <ActionFooter
                  className={styles.formBtn}
                  disabled={props.isSubmitting}
                  onCancel={() => {
                    props.resetForm();
                    history.push({
                      ...location,
                      pathname: "/settings",
                    });
                  }}
                  onAction={props.submitForm}
                  title={props.isSubmitting ? t("gogetter.settings.saving") : t("gogetter.settings.save")}
                />
              </FooterDistance>
            </>
          );
        }}
      </Formik>
    </div>
  );
}
